/**
 * Learning Path Content - Main content area for learning path details
 * Handles course selection and displays course/lesson content with progress
 */
'use client';

import { useMemo, useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import Note from '@/components/common/note';
import InfoBanner from '@/components/common/info-banner';
import CourseMeta from '@/components/course-meta/course-meta';
import { CoursesLeftSidebar } from '@/components/sidebars/courses-sidebar';
import LessonsMeta from '@/components/sidebars/lessons-meta';
import LPGatePrompt from '@/components/learning-paths/lp-gate-prompt';
import { AssessmentResults } from '@/components/assessment/AssessmentResults';
import { getCertificateDataByLpID } from '@/lib/api/certificate-api';
import { getUserData } from '@/helpers/local-storage';
import { Course as FullCourse } from '@/types/course';
import { useLearningPathLessonProgress, CourseWithProgress } from '@/hooks/useUserLearningPaths';
import { UserLearningPath } from '@/types/learning-paths';
import { UserCourse } from '@/types/user-course-lessons';
import { CMSLearningPath } from '@/types/cms-lp';
import { getTotalCourseDuration } from '@/lib/utils/duration';
import { getResumeUrl } from '@/lib/utils/get-resume-url';
import type { AssessmentResult } from '@/components/assessment/types';
import { useAssessmentData as useAssessmentStore } from '@/store/assessment-store';

type SelectedView = 'course' | 'assessment';

/** Normalized course-wise result item */
const COURSE_WISE_KEYS = ['courseUid', 'course_uid'] as const;
const COUNT_KEYS = ['correct', 'incorrect', 'total'] as const;

function looksLikeCourseWiseItem(o: unknown): o is Record<string, unknown> {
  if (!o || typeof o !== 'object') return false;
  const obj = o as Record<string, unknown>;
  const hasCourseKey = COURSE_WISE_KEYS.some((k) => obj[k] != null);
  const hasCounts = COUNT_KEYS.every((k) => typeof obj[k] === 'number' || (typeof obj[k] === 'string' && /^\d+$/.test(String(obj[k]))));
  return !!hasCourseKey && (hasCounts || COUNT_KEYS.some((k) => obj[k] != null));
}

function normalizeCourseWiseList(arr: unknown[]): { courseUid: string; correct: number; incorrect: number; total: number }[] {
  return arr.map((item: unknown) => {
    const o = (item as Record<string, unknown>) ?? {};
    return {
      courseUid: String(o.courseUid ?? o.course_uid ?? ''),
      correct: Number(o.correct ?? 0),
      incorrect: Number(o.incorrect ?? 0),
      total: Number(o.total ?? 0),
    };
  });
}

/**
 * Deep-scan an object for an array that looks like course-wise results (each item has courseUid/course_uid, correct, incorrect, total).
 * Use when API returns course breakdown under unknown or nested keys.
 */
function findCourseWiseResultsInPayload(
  obj: unknown,
  seen = new WeakSet<object>()
): { courseUid: string; correct: number; incorrect: number; total: number }[] | undefined {
  if (obj == null) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (seen.has(obj as object)) return undefined;
  seen.add(obj as object);

  if (Array.isArray(obj)) {
    if (obj.length > 0 && obj.every(looksLikeCourseWiseItem)) {
      return normalizeCourseWiseList(obj);
    }
    for (const item of obj) {
      const found = findCourseWiseResultsInPayload(item, seen);
      if (found?.length) return found;
    }
    return undefined;
  }

  const record = obj as Record<string, unknown>;
  for (const value of Object.values(record)) {
    const found = findCourseWiseResultsInPayload(value, seen);
    if (found?.length) return found;
  }
  return undefined;
}

export default function LearningPathContent({
  learningPath,
  learningPathData,
  courses,
  userLpData,
}: {
  learningPath: string;
  learningPathData: CMSLearningPath;
  courses: CourseWithProgress[];
  userLpData?: UserLearningPath;
}) {
  // User enrollment and progress state
  const isSubscribed = userLpData?.isSubscribed ?? false;
  const hasStarted = userLpData?.progress?.hasStarted ?? false;
  // isCompleted: API only — true when user passed assessment (LP fully done). Do NOT use allCoursesCompleted.
  const isCompleted = userLpData?.progress?.isCompleted === true;
  const allCoursesCompleted =
    courses.length > 0 &&
    courses.every(
      (c) => c.isCompleted || (c.userProgress?.percentage ?? 0) >= 100
    );

  const [selectedCourse, setSelectedCourse] = useState<CourseWithProgress | null>(null);
  const [selectedView, setSelectedView] = useState<SelectedView>('course');

  const hasAssessment = learningPathData?.is_assessment === true;

  // Reset to course view when learning path has no assessment
  useEffect(() => {
    if (!hasAssessment && selectedView === 'assessment') {
      setSelectedView('course');
      setSelectedCourse(courses[0] ?? null);
    }
  }, [hasAssessment, selectedView, courses]);

  // Fetch lesson progress for selected course
  const { data: chaptersWithProgress } = useLearningPathLessonProgress(
    selectedCourse?.uid,
    selectedCourse || undefined
  );

  // Merge course with progress data
  const selectedCourseWithProgress = useMemo(() => {
    if (!selectedCourse) return null;

    return {
      ...selectedCourse,
      chapters: chaptersWithProgress.length > 0 ? chaptersWithProgress : selectedCourse.chapters,
    };
  }, [selectedCourse, chaptersWithProgress]);

  // Find user course data for the selected course (for bookmark state)
  const selectedUserCourseData = useMemo((): Pick<UserCourse, 'isBookmarked'> | null => {
    if (!selectedCourse || !userLpData?.courses) return null;
    const course = userLpData.courses.find((c) => c.entryUid === selectedCourse.uid);
    if (!course) return null;
    // Return only the fields needed by CourseMeta
    return { isBookmarked: course.isBookmarked };
  }, [selectedCourse, userLpData]);

  // Handle course/assessment selection from sidebar
  const handleCourseClick = useCallback(
    (
      item: CourseWithProgress | { uid?: string; title?: string; url: string } | null | undefined
    ) => {
      if (item === null) {
        // Assessment was clicked
        setSelectedCourse(null);
        setSelectedView('assessment');
      } else if (item && item.uid) {
        // Find the full course object from our courses array
        const course = courses.find((c) => c.uid === item.uid);
        if (course) {
          setSelectedCourse(course);
          setSelectedView('course');
        }
      }
    },
    [courses]
  );

  // Calculate resume URL based on user progress
  const resumeUrl = useMemo(() => {
    return getResumeUrl({
      learningPathData,
      userLpData: userLpData || null,
      courses,
    });
  }, [learningPathData, userLpData, courses]);

  // Assessment completed (has submitted attempt) – show same result card as on assessment page
  const assessmentFromProgress = userLpData?.progress?.assessment;
  const hasAssessmentResult =
    selectedView === 'assessment' &&
    !!assessmentFromProgress?.attemptLog?.length;

  // Store result (from submit) may have courseWiseResults when progress API does not
  const storedResult = useAssessmentStore((s) => (learningPathData?.uid ? s.assessments[learningPathData.uid]?.result : undefined));

  const assessmentResult = useMemo((): AssessmentResult | null => {
    if (!hasAssessmentResult || !assessmentFromProgress) return null;
    const progressAssessment = assessmentFromProgress as unknown as Record<string, unknown>;
    const rawLast = assessmentFromProgress.attemptLog?.[assessmentFromProgress.attemptLog.length - 1] as Record<string, unknown> | undefined;
    const userBadgeData = (userLpData as { userBadgeData?: { uid: string; badgeReferenceUid?: string } })?.userBadgeData;

    // API may return course_wise_results (snake_case) or courseWiseResults (camelCase) in last attempt or at assessment top level
    let rawCourseWise =
      rawLast?.courseWiseResults ??
      rawLast?.course_wise_results ??
      progressAssessment.courseWiseResults ??
      progressAssessment.course_wise_results;

    // Fallback: use store result (set after submit) when progress API doesn't include course-wise data
    if (!Array.isArray(rawCourseWise) && storedResult) {
      const stored = storedResult as Record<string, unknown>;
      const storedLast = (stored.attemptLog as unknown[])?.[(stored.attemptLog as unknown[])?.length - 1] as Record<string, unknown> | undefined;
      rawCourseWise =
        stored.courseWiseResults ??
        stored.course_wise_results ??
        storedLast?.courseWiseResults ??
        storedLast?.course_wise_results;
    }

    // Fallback: deep-scan progress and LP payload for any array shaped like course-wise results (API may use different keys/nesting)
    if (!Array.isArray(rawCourseWise)) {
      const fromProgress = findCourseWiseResultsInPayload(progressAssessment);
      const fromLp = userLpData ? findCourseWiseResultsInPayload(userLpData as object) : undefined;
      const fromStored = storedResult ? findCourseWiseResultsInPayload(storedResult as object) : undefined;
      rawCourseWise = fromProgress ?? fromLp ?? fromStored ?? undefined;
    }

    const courseWiseResults = Array.isArray(rawCourseWise)
      ? rawCourseWise.map((item: Record<string, unknown>) => ({
          courseUid: String(item.courseUid ?? item.course_uid ?? ''),
          correct: Number(item.correct ?? 0),
          incorrect: Number(item.incorrect ?? 0),
          total: Number(item.total ?? 0),
        }))
      : undefined;

    // Attach normalized courseWiseResults to last attempt so AssessmentResults finds it (same shape as assessment page)
    const attemptLog = Array.isArray(assessmentFromProgress.attemptLog)
      ? assessmentFromProgress.attemptLog.map((entry: Record<string, unknown>, i: number) =>
          i === assessmentFromProgress.attemptLog!.length - 1 && courseWiseResults?.length
            ? { ...entry, courseWiseResults }
            : entry
        )
      : assessmentFromProgress.attemptLog;

    return {
      ...assessmentFromProgress,
      lastAttemptedAt: assessmentFromProgress.lastAttemptedAt ?? undefined,
      attemptLog: attemptLog as AssessmentResult['attemptLog'],
      percentageAchieved: (rawLast?.percentageAchieved ?? rawLast?.percentage_achieved ?? assessmentFromProgress.maxPercentageAchieved) as number,
      totalMarks: (rawLast?.totalMarks ?? rawLast?.total_marks) as number | undefined,
      totalQuestions: (rawLast?.totalQuestions ?? rawLast?.total_questions) as number | undefined,
      courseWiseResults: courseWiseResults?.length ? courseWiseResults : undefined,
      badgeData: userBadgeData,
    } as AssessmentResult;
  }, [hasAssessmentResult, assessmentFromProgress, userLpData, storedResult, learningPathData?.uid]);

  const assessmentConfig = useMemo(
    () => ({
      passing_criteria: (learningPathData as { assessment?: { passing_criteria?: number } })?.assessment?.passing_criteria ?? 70,
    }),
    [learningPathData]
  );

  const assessmentCurrentScore = useMemo(() => {
    const ap = userLpData?.progress?.assessment;
    if (!ap?.attemptLog?.length) return null;
    const last = ap.attemptLog[ap.attemptLog.length - 1] as Record<string, unknown> | undefined;
    const pct = last?.percentageAchieved ?? last?.percentage_achieved;
    return typeof pct === 'number' ? pct : null;
  }, [userLpData?.progress?.assessment]);

  const learningPathUrl = learningPathData?.url || `/${learningPath}`;
  const achievementType = ((learningPathData as { learning_pathway_details?: { awards_certifications?: string } })?.learning_pathway_details?.awards_certifications as 'certificate' | 'badge' | 'none') || 'none';

  const assessmentPassed =
    !!assessmentResult &&
    (assessmentResult.percentageAchieved ?? assessmentResult.attemptLog?.at(-1)?.percentageAchieved ?? 0) >=
      (assessmentConfig.passing_criteria ?? 70);

  const { data: certificateData } = useQuery({
    queryKey: ['certificate-by-lp', learningPathData?.uid],
    queryFn: () => getCertificateDataByLpID(learningPathData!.uid),
    enabled: !!hasAssessmentResult && assessmentPassed && achievementType === 'certificate' && !!learningPathData?.uid,
  });

  const router = useRouter();
  const clearAssessment = useAssessmentStore((s) => s.clearAssessment);
  const setAssessmentResult = useAssessmentStore((s) => s.setAssessmentResult);

  const handleRetakeAssessment = useCallback(() => {
    const uid = learningPathData?.uid;
    if (uid) {
      setAssessmentResult(uid, null);
      clearAssessment(uid);
    }
    router.push(`/learning-paths/${learningPath}/assessment?retake=true`);
  }, [router, learningPath, learningPathData?.uid, clearAssessment, setAssessmentResult]);

  const assessmentCourses = useMemo(
    () =>
      courses.map((c) => ({
        title: c.title ?? '',
        url: c.url ?? '',
        uid: c.uid ?? '',
      })),
    [courses]
  );

  const assessmentCourseDetails = useMemo(
    () =>
      courses.map((c) => {
        const thumb = (c as { thumbnail?: { url?: string } }).thumbnail?.url;
        return {
          ...(c as unknown as Record<string, unknown>),
          uid: c.uid ?? '',
          title: c.title ?? '',
          url: c.url ?? '',
          thumbnail: thumb ? { url: thumb } : undefined,
        };
      }),
    [courses]
  );

  return (
    <div className="flex flex-col flex-nowrap gap-4 md:flex-row">
      {/* 1 */}
      <div className="order-1 w-full self-start md:sticky md:top-24 md:order-1 md:w-[30%]">
        <CoursesLeftSidebar
          courses={courses}
          selectedCourse={selectedCourse}
          onCourseClick={handleCourseClick}
          isAssessmentSelected={selectedView === 'assessment'}
          assessmentCurrentScore={assessmentCurrentScore}
          showAssessment={hasAssessment}
        />
      </div>

      {/* 2 */}
      {false ? (
        <InfoBanner
          title="You are not currently enrolled."
          description="Click the button below to start tracking your <strong className='text-[var(--Text-Default-Strong)]'>Contentstack Developer</strong> progress."
          buttonTitle="Enroll now"
          buttonUrl="#"
        />
      ) : (
        <div className="order-2 w-full md:order-2 md:w-[70%]">
          {selectedView === 'course' && selectedCourseWithProgress ? (
            <>
              {!isSubscribed && (
                <Note
                  title="You are not currently enrolled in this Learning Path"
                  copy="Make sure you enroll in order to track your progress through this Learning Path."
                />
              )}
              <CourseMeta
                course={selectedCourseWithProgress as unknown as FullCourse}
                duration={getTotalCourseDuration(selectedCourseWithProgress) || ''}
                userCourseData={selectedUserCourseData as UserCourse | null}
                copyUrl={(() => {
                  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '/academy';
                  const cleanBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
                  return `${cleanBase}/learning-paths/${learningPath}${selectedCourseWithProgress?.url || ''}`;
                })()}
              />
              <LessonsMeta
                selectedCourse={selectedCourseWithProgress?.url || ''}
                url={`/learning-paths/${learningPath}${selectedCourseWithProgress?.url || ''}`}
                courses={chaptersWithProgress?.[0]?.lessons?.lessons || []}
              />
            </>
          ) : hasAssessmentResult && assessmentResult ? (
            <AssessmentResults
              result={assessmentResult}
              assessment={assessmentConfig}
              assessmentName={
                (learningPathData?.learning_pathway_details?.headline as string) ||
                (learningPathData?.title as string) ||
                ''
              }
              learningPathUrl={learningPathUrl}
              updatedAt={learningPathData?.updated_at ?? learningPathData?.created_at ?? ''}
              totalQuestions={
                assessmentResult?.totalQuestions ??
                assessmentResult?.attemptLog?.at(-1)?.totalQuestions ??
                0
              }
              topicsData={[]}
              courses={assessmentCourses}
              courseDetails={assessmentCourseDetails}
              user={getUserData()}
              achievementType={achievementType}
              certificateData={certificateData ?? undefined}
              onRetake={handleRetakeAssessment}
              compact
            />
          ) : (
            <LPGatePrompt
              learningPathUid={learningPathData?.uid}
              learningPathUrl={learningPathUrl}
              isSubscribed={isSubscribed}
              hasStarted={hasStarted}
              isCompleted={isCompleted}
              allCoursesCompleted={allCoursesCompleted}
              progressPercent={allCoursesCompleted ? 100 : userLpData?.progress?.percentage}
              isAssessmentView={selectedView === 'assessment'}
              learningPathTitle={
                (learningPathData?.learning_pathway_details?.headline as string) ||
                (learningPathData?.title as string) ||
                undefined
              }
              resumeUrl={resumeUrl}
            />
          )}
        </div>
      )}
    </div>
  );
}
